import { gql } from "@apollo/client";

const OPT_OUT_NUMBER_DETAILS_FIELDS = gql`
  fragment OptOutNumberDetailsFields on OptOutNumberDetails {
    phoneNumber
    isTextable
    optOuts {
      ... on OptOut {
        id
        source {
          ... on OptOutAdminListSource {
            list {
              id
              name
              createdByUser {
                id
                fullName
              }
            }
          }
          ... on OptOutCampaignAutomaticSource {
            message {
              id
              text
              campaignName
              threadOrderTime
            }
          }
          ... on OptOutShortCodeKeywordSource {
            ownedPhoneNumber {
              id
              phoneNumber
            }
          }
          ... on OptOutCampaignManualSource {
            campaign {
              id
              name
            }
            user {
              id
              fullName
            }
          }
        }
        createdAt
        deletedAt
        subaccount {
          id
          name
          slug
        }
      }
      ... on AnonymousOptOut {
        id
        anonymousOptOutType
        createdAt
        deletedAt
      }
    }
    temporaryOptOuts {
      ... on TemporaryOptOut {
        id
        phoneNumber
        source {
          ... on TemporaryOptOutAdminListSource {
            list {
              id
              name
              createdByUser {
                id
                fullName
              }
            }
          }
        }
        createdAt
        expiresAt
        deletedAt
        subaccount {
          id
          name
          slug
        }
      }
      ... on AnonymousTemporaryOptOut {
        id
        anonymousOptOutType
        createdAt
        expiresAt
        deletedAt
      }
    }
  }
`;

const OPT_OUT_LIST_FIELDS = gql`
  fragment OptOutListFields on OptOutList {
    id
    type
    subaccount {
      id
      name
      slug
    }
    name
    createdByUser {
      id
      fullName
    }
    createdAt
    deletedAt
    expiresAt
    count
    importJob {
      ... on AdminOptOutListImportJob {
        id
        status
        result {
          nContacts
          nInvalid
          nDuplicates
          nAlreadyOptedOut
          nUploaded
        }
        errorMessage
      }
    }
    editExpirationJob {
      ... on EditOptOutListExpirationJob {
        id
        status
        errorMessage
      }
    }
    upload {
      id
      fileName
    }
  }
`;

export const GET_SUBACCOUNT_OPT_OUT_NUMBER_DETAILS = gql`
  ${OPT_OUT_NUMBER_DETAILS_FIELDS}
  query subaccountOptOutNumberDetails(
    $input: SubaccountOptOutNumberDetailsInput!
  ) {
    subaccountOptOutNumberDetails(input: $input) {
      ...OptOutNumberDetailsFields
    }
  }
`;

export const GET_TENANT_OPT_OUT_NUMBER_DETAILS = gql`
  ${OPT_OUT_NUMBER_DETAILS_FIELDS}
  query tenantOptOutNumberDetails($input: TenantOptOutNumberDetailsInput!) {
    tenantOptOutNumberDetails(input: $input) {
      ...OptOutNumberDetailsFields
    }
  }
`;

export const DELETE_OPT_OUT_LIST_JOB_FIELDS = gql`
  ${OPT_OUT_LIST_FIELDS}
  fragment DeleteOptOutListJobFields on DeleteOptOutListJob {
    id
    status
    finishedAt
    errorMessage
    args {
      list {
        ...OptOutListFields
      }
    }
    result {
      listCount
      textableCount
      optedOutGlobally
      optedOutAutomatically
      optedOutAutomaticallyOtherSubaccount
      optedOutByTexter
      optedOutByTexterOtherSubaccount
      optedOutInList
      optedOutInListOtherSubaccount
      optedOutTemporarily
      optedOutInSpoke
      optedOutInSpokeOtherSubaccount
      optedOutInSpokeGlobally
      optedOutShortCode
      optedOutShortCodeOtherSubaccount
    }
  }
`;

export const GET_SUBACCOUNT_OPT_OUT_SUMMARY = gql`
  ${OPT_OUT_LIST_FIELDS}
  ${DELETE_OPT_OUT_LIST_JOB_FIELDS}
  query subaccountOptOutSummary($input: SubaccountOptOutSummaryInput!) {
    subaccountOptOutSummary(input: $input) {
      permanentListOptOutCount
      temporaryListOptOutCount
      autoOptOutCount
      globalOptOutCount
      sharedSubaccountOptOutCount
      spokeOptOutCount
      texterOptOutCount
      countsComputedAt
      refreshing
      optOutLists {
        ...OptOutListFields
      }
      deleteOptOutListJobs {
        ...DeleteOptOutListJobFields
      }
    }
  }
`;

export const GET_GLOBAL_OPT_OUT_SUMMARY = gql`
  ${OPT_OUT_LIST_FIELDS}
  ${DELETE_OPT_OUT_LIST_JOB_FIELDS}
  query globalOptOutSummary {
    globalOptOutSummary {
      permanentListOptOutCount
      countsComputedAt
      refreshing
      optOutLists {
        ...OptOutListFields
      }
      deleteOptOutListJobs {
        ...DeleteOptOutListJobFields
      }
    }
  }
`;

export const UPLOAD_ADMIN_OPT_OUT_LIST = gql`
  ${OPT_OUT_LIST_FIELDS}
  mutation uploadAdminOptOutList($input: UploadAdminOptOutListInput!) {
    uploadAdminOptOutList(input: $input) {
      ...OptOutListFields
    }
  }
`;

export const UPDATE_OPT_OUT_LIST = gql`
  mutation updateOptOutList($input: UpdateOptOutListInput!) {
    updateOptOutList(input: $input) {
      id
      name
      expiresAt
      editExpirationJob {
        ... on EditOptOutListExpirationJob {
          id
          status
          errorMessage
        }
      }
    }
  }
`;

export const OPT_OUTS_IN_LIST = gql`
  query optOutsInList($input: OptOutsInListInput!) {
    optOutsInList(input: $input) {
      ... on OptOut {
        id
        phoneNumber
        deletedAt
        createdAt
        createdByUser {
          id
          fullName
        }
      }
      ... on TemporaryOptOut {
        id
        phoneNumber
        deletedAt
        createdAt
        createdByUser {
          id
          fullName
        }
      }
    }
  }
`;

export const SEARCH_OPT_OUTS = gql`
  query searchOptOuts($input: SearchOptOutsInput!) {
    searchOptOuts(input: $input) {
      optOutLists {
        id
        name
      }
      optedOutNumbers
    }
  }
`;

export const DELETE_OPT_OUT_LIST = gql`
  mutation deleteOptOutList($input: DeleteOptOutListInput!) {
    deleteOptOutList(input: $input) {
      id
    }
  }
`;

export const GET_OPT_OUT_LISTS = gql`
  ${OPT_OUT_LIST_FIELDS}
  query OptOutLists($input: OptOutListsInput!) {
    optOutLists(input: $input) {
      ...OptOutListFields
    }
  }
`;

export const ADMIN_MANUAL_DELETE_OPT_OUT = gql`
  mutation AdminManualDeleteOptOut(
    $input: AdminManualDeleteOptOutsForNumberInput!
  ) {
    adminManualDeleteOptOutsForNumber(input: $input) {
      success
    }
  }
`;

export const CAMPAIGN_MANUAL_DELETE_OPT_OUT = gql`
  mutation CampaignManualDeleteOptOut(
    $input: CampaignManualDeleteOptOutsForThreadInput!
  ) {
    campaignManualDeleteOptOutsForThread(input: $input) {
      success
    }
  }
`;

export const REFRESH_SUBACCOUNT_OPT_OUT_COUNTS = gql`
  mutation RefreshSubaccountOptOutCounts(
    $input: RefreshSubaccountOptOutCountsInput!
  ) {
    refreshSubaccountOptOutCounts(input: $input) {
      success
    }
  }
`;

export const REFRESH_TENANT_OPT_OUT_COUNTS = gql`
  mutation RefreshTenantOptOutCounts {
    refreshTenantOptOutCounts {
      success
    }
  }
`;
